import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  IGenericDataFetchFieldsConfigFormModel,
} from '../../../models/form-builder-types-configurations.model';

import { FormBuilderService } from '../../../services/form-builder.service';
import {
  generateDatasetSettingsConfig,
  generateNonPaginatedDataSetSettingsConfig,
  genericDataFieldFieldParseIntoSettingsFormModel,
  genericDataFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from 'src/app/core/models/form-fields-formly-validation.model';
import { parseRequiredValidatorParseFieldIntoModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import { setRequiredValidatorUpdateFieldFromModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { IFormFieldsDisplayModel } from 'src/app/core/models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { getRequiredValidatorFormConfig } from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';

export const defaultCustomDropDownWithDataSetConfigFormModel: IGenericDataFetchFieldsConfigFormModel =
  {
    fieldLabel: null,
    placeholder: null,
    hint: null,
    tooltip: null,
    internalDescription: null,
    internal: true,
    readonly: false,
    disabled: false,
    options: [],
    datasetSection: {
      url: null,
      dataField: null,
      bindValue: 'value',
      bindLabel: 'label',
      customUrl: null,
      code: null,
    },
  };

export const customDropDownWithDatasetFieldGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultCustomDropDownWithDataSetConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        {
          key: 'readonly',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Read Only',
            hideHeader: true,
            placeholder: 'Read Only',
            required: false,
            formCheck: 'switch',
          },
          id: 'readonly',
        },
        {
          key: 'disabled',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Disabled',
            hideHeader: true,
            placeholder: 'Disabled',
            required: false,
            formCheck: 'switch',
          },
          id: 'disabled',
        },
        generateDatasetSettingsConfig(),
      ],
    },
    validations: {
      formModel: {
        minLength: '',
        required: '',
        maxLength: '',
      },
      formlyForm: [getRequiredValidatorFormConfig()],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const customDropDownWithDataSetFieldNonPaginatedGetConfigurationConfig =
  (
    formBuilderServiceInstance: FormBuilderService
  ): IFormbuilderFieldTypeConfiguration => {
    return {
      settings: {
        formModel: defaultCustomDropDownWithDataSetConfigFormModel,
        formlyForm: [
          ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
          {
            key: 'readonly',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Read Only',
              hideHeader: true,
              placeholder: 'Read Only',
              required: false,
              formCheck: 'switch',
            },
            id: 'readonly',
          },
          {
            key: 'disabled',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Disabled',
              hideHeader: true,
              placeholder: 'Disabled',
              required: false,
              formCheck: 'switch',
            },
            id: 'disabled',
          },
          generateNonPaginatedDataSetSettingsConfig(),
        ],
      },
      validations: {
        formModel: {
          minLength: '',
          required: '',
          maxLength: '',
        },
        formlyForm: [getRequiredValidatorFormConfig()],
      },
      display: {
        formModel: {},
        formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
      },
    };
  };

export const customDropDownWithDatasetFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IGenericDataFetchFieldsConfigFormModel => {
  const model = genericDataFieldFieldParseIntoSettingsFormModel(field);
  const savedDatasetValue = model.datasetSection.code;
  if (typeof savedDatasetValue !== 'string' || !savedDatasetValue) return model;

  const persistedDatasetName = field.props?.['datasetName'] as
    | string
    | undefined;
  const isLegacyPaginatedSelection =
    field.type === EIremboFormlyFieldTypes.customdropdownpaginated &&
    !persistedDatasetName;
  (model.datasetSection as any).code =
    field.type === EIremboFormlyFieldTypes.customdropdownpaginated
      ? {
          id: savedDatasetValue,
          name: persistedDatasetName ?? 'Loading selected dataset...',
          __legacySelection: isLegacyPaginatedSelection,
        }
      : {
          code: savedDatasetValue,
          name: persistedDatasetName ?? formatDatasetName(savedDatasetValue),
        };
  return model;
};

export const customDropDownWithDatasetFieldUpdateFromSettingsFormModel = (
  model: IGenericDataFetchFieldsConfigFormModel,
  dropdownField: FormlyFieldConfig
): FormlyFieldConfig => {
  const selectedDataset = (model.datasetSection as any).code;
  const selectedDatasetValue =
    selectedDataset && typeof selectedDataset === 'object'
      ? dropdownField.type === EIremboFormlyFieldTypes.customdropdownpaginated
        ? selectedDataset['id']
        : selectedDataset['code']
      : selectedDataset;
  const selectedDatasetName =
    selectedDataset &&
    typeof selectedDataset === 'object' &&
    !selectedDataset['__legacySelection']
      ? selectedDataset['name']
      : undefined;
  const normalizedModel: IGenericDataFetchFieldsConfigFormModel = {
    ...model,
    datasetSection: {
      ...model.datasetSection,
      code:
        typeof selectedDatasetValue === 'string' ? selectedDatasetValue : null,
    },
  };
  const updatedField = genericDataFieldUpdateFromSettingsFormModel(
    normalizedModel,
    dropdownField
  );
  if (updatedField.props) {
    if (typeof selectedDatasetName === 'string') {
      updatedField.props['datasetName'] = selectedDatasetName;
    }
  }
  return updatedField;
};

const formatDatasetName = (value: string): string => {
  if (!/^[A-Z0-9_-]+$/.test(value)) return value;
  return value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
};

export const customDropWithDatasetFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  return model;
};

export const customDropWithDatasetFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = setRequiredValidatorUpdateFieldFromModel(model, field);
  return field;
};

export const customDropDownWithDatasetFieldDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const customDropDownWithDatasetUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};
